// ============================================================
// Toaster.js
// Lightweight toast notification system. Toasts auto-dismiss
// after 4s (success) / 6s (error); error toasts are also
// manually dismissable.
//
// Public API (imported anywhere):
//   import { toast } from './Toaster';
//   toast.success('Tenant created');
//   toast.error('Could not load tenants');
//
// Mount <Toaster /> once at the app root (inside AppShell or
// App.js). It subscribes to the toast event bus and renders
// the active toasts in a fixed-position list.
//
// Implementation uses a tiny event emitter rather than React
// context — the toast() API is callable from anywhere without
// threading providers, and there's only ever one Toaster
// mounted, so a singleton works fine. Same idea as react-hot-toast.
// ============================================================

import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import './Toaster.css';

const SUCCESS_TIMEOUT_MS = 4000;
const ERROR_TIMEOUT_MS   = 6000;

// ── Event bus ─────────────────────────────────────────────────

const listeners = new Set();
let nextId = 1;

function emit(toast) {
  listeners.forEach((fn) => fn(toast));
}

export const toast = {
  success(message) {
    emit({ id: nextId++, type: 'success', message });
  },
  error(message) {
    emit({ id: nextId++, type: 'error', message });
  },
};

// ── Component ─────────────────────────────────────────────────

export default function Toaster() {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    function handler(t) {
      setToasts((current) => [...current, t]);
      const timeout = t.type === 'error' ? ERROR_TIMEOUT_MS : SUCCESS_TIMEOUT_MS;
      const timer = setTimeout(() => {
        setToasts((current) => current.filter((x) => x.id !== t.id));
        timersRef.current.delete(t.id);
      }, timeout);
      timersRef.current.set(t.id, timer);
    }
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
    };
  }, []);

  function dismiss(id) {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((x) => x.id !== id));
  }

  return (
    <div className="toaster" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.type}`}
          role={t.type === 'error' ? 'alert' : 'status'}
        >
          {t.type === 'success'
            ? <CheckCircle2 size={18} className="toast__icon" />
            : <AlertCircle size={18} className="toast__icon" />}
          <span className="toast__message">{t.message}</span>
          <button
            type="button"
            className="toast__dismiss"
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss notification"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
